import { useStoreState } from 'easy-peasy';
import { useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';

import { QueryKey } from '@ge/models/constants';
import { useTableFilter } from '@ge/shared/data-hooks/use-table-filter';
import {
  getEventManagementList,
  createEventManagement,
  updateEventManagement,
  putEventManagement,
  getProcProperties,
  getAllEventCodes,
  getAutomationSequences,
} from '@ge/shared/services/event-management';
import { sorter } from '@ge/util/metric-sorter';

import { Config } from './config';

export const useEM = ({ search, filters, sortDirection, sortMetric, mapId }) => {
  const { getSiteById } = useStoreState((state) => state.sites);

  let { data, error, isLoading, isFetching } = useQuery(
    [QueryKey.EVENT_MANAGEMENT_LIST, mapId],
    () => getEventManagementList({ mapId }),
    {
      refetchOnMount: true,
      refetchOnReconnect: true,
      enabled: mapId != null ? true : false,
    },
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tempFilterValues = { includedsites: [], excludedsites: [], procedurehandling: [], notes: [], eventmap: [], automationsequence: []};
  const recordsFn = useMemo(() => {
    const records = [];
    data && data.length && data.forEach((eventMap) => {
      eventMap?.events?.forEach((rec) => {
        const value = rec;
        if (value?.flaggedProperties) {
          ['include', 'exclude'].forEach((record) => {
            if (value.flaggedProperties[`${record}SitesFilter`]?.length) {
              value[`${record}dsites`] = {
                value: value.flaggedProperties[`${record}SitesFilter`]
                  ?.map((val) => getSiteById(val.siteId)?.name)
                  .filter((val) => !!val),
              };
              tempFilterValues[`${record}dsites`] = [
                ...(tempFilterValues[`${record}dsites`] || []),
                ...value[`${record}dsites`]?.value,
              ];
            }
          });
        }
        if(value?.automationProperties?.procedure){
            value['automationsequence'] = {
                  value: value?.automationProperties?.procedure.map(Obj => Obj.name?.replaceAll('_', ' '))
                }
        }
        value.sequenceName = (value?.automationsequence?.value.join(', ') || '');
        value.blockCommands = (value?.commandProperties?.blockCommands || false).toString();
        value.blockAutomation = (value?.automationProperties?.blockAutomation || false).toString();
        value.maxResetLimit = (value?.commandProperties?.maxResetLimit || 0).toString();
        value.presentationDelay = (value?.caseProperties?.presentationDelay || 0).toString();
        value.caseCreationDelay = (value?.caseProperties?.caseCreationDelay || 0).toString();
        value.flaggedStates = { value: value?.flaggedProperties?.eligableStates || [] };
        value.procedurehandling = (value?.procProperties?.procTitle || '')
        value.eventMapName = (eventMap?.eventMapName || '')
        value.eventMapId = (eventMap?.eventMapId || '')
        value.notes = (value?.note || '')
        value.eventmap = (eventMap?.eventMapName || '')
        tempFilterValues.flaggedStates = [
          ...(tempFilterValues.flaggedStates || []),
          ...(value?.flaggedStates?.value || []),
        ];
        tempFilterValues.procedurehandling = [
          ...(tempFilterValues.procedurehandling || []), (value?.procedurehandling || '')
        ]
        tempFilterValues.notes = [
          ...(tempFilterValues.notes || []), (value?.notes || '')
        ]
        tempFilterValues.eventmap = [
          ...(tempFilterValues.eventmap || []), (value?.eventmap || '')
        ]
        tempFilterValues.automationsequence = [
          ...(tempFilterValues.automationsequence || []),
          ...(value?.automationsequence?.value || []),
        ];
        records.push(value);
      });
    })
    return records;
  }, [data, getSiteById, tempFilterValues]);
  if (filters?.code?.value) {
    recordsFn?.forEach((data) => (data.code = String(data.code)));
  } else {
    recordsFn?.forEach((data) => (data.code = Number(data.code)));
  }
  const { data: filteredData, filterValues } = useTableFilter({
    data: recordsFn,
    filters,
    search,
    filterSequence: [],
    isFleetTable : true,
  });

  if (filterValues) {
    filterValues.includedsites = [...new Set(tempFilterValues.includedsites)];
    filterValues.excludedsites = [...new Set(tempFilterValues.excludedsites)];
    filterValues.flaggedStates = [...new Set(tempFilterValues.flaggedStates)];
    filterValues.procedurehandling = [...new Set(tempFilterValues.procedurehandling)];
    filterValues.notes = [...new Set(tempFilterValues.notes)];
    filterValues.eventmap = [...new Set(tempFilterValues.eventmap)];
    filterValues.automationsequence = [...new Set(tempFilterValues.automationsequence)];
  }
  const code = filterValues?.code?.map(String);
  // cuts a new ref when sorting to update graph accordingly
  const sortedData = useMemo(
    () => (filteredData?.length ? [...filteredData.sort(sorter(sortMetric, sortDirection))] : []),
    [filteredData, sortDirection, sortMetric],
  );
  return {
    eventManagementLists: sortedData,
    filterValues: { ...filterValues, code: code },
    error,
    isLoading,
    isFetching,
  };
};

export const useEventValidationList = ({ id, mapId }) => {
  let { data, error, isLoading } = useQuery(
    [QueryKey.EVENT_MANAGEMENT_LIST, id, mapId],
    () => getEventManagementList({ id, mapId }),
    {
      ...Config.EXECUTE_ONCE,
      enabled: !!(id && mapId),
    },
  );

  let details = data && data[0]?.events[0];
  return { isExist: data?.length, error, isLoading, details };
};

export const useCreateEventManagement = () => {
  const queryCache = useQueryClient();

  const {
    mutateAsync: createEM,
    data,
    isLoading,
    isError,
    error,
  } = useMutation(createEventManagement, {
    onSuccess: () => queryCache.invalidateQueries(QueryKey.EVENT_MANAGEMENT_LIST, { exact: true }),
    enabled: true,
    ...Config.EXECUTE_ONCE,
  });
  return useMemo(
    () => ({ createEM, data, isLoading, isError, error }),
    [createEM, data, isLoading, isError, error],
  );
};

export const useUpdateEventManagement = () => {
  const queryCache = useQueryClient();

  const {
    mutateAsync: updateEM,
    data,
    isLoading,
    isError,
    error,
  } = useMutation(putEventManagement, {
    onSuccess: () => queryCache.invalidateQueries(QueryKey.EVENT_MANAGEMENT_LIST),
    enabled: true,
    ...Config.EXECUTE_ONCE,
  });

  return useMemo(
    () => ({ updateEM, data, isLoading, isError, error }),
    [updateEM, data, isLoading, isError, error],
  );
};

export const useProcProperties = (id) => {
  let { data } = useQuery([QueryKey.EVENT_MANAGEMENT_LIST, id], () => getProcProperties({ id }), {
    ...Config.EXECUTE_ONCE,
    enabled: !!id,
    select: (data) => data[0],
  });

  return { data };
};

export const useAllAutomationSequence = (eventCode) => {
  let { data } = useQuery([QueryKey.AUTOMATION_SEQUENCE_LIST, eventCode], 
    () => getAutomationSequences(), {
    ...Config.EXECUTE_ONCE,
    enabled: eventCode,
  });

  return { data };
};

export const useEditEventManagement = () => {
  const queryCache = useQueryClient();

  const {
    mutateAsync: editEM,
    data,
    isLoading,
    isError,
    error,
  } = useMutation(updateEventManagement, {
    onSuccess: () => queryCache.invalidateQueries(QueryKey.EVENT_MANAGEMENT_LIST),
    enabled: true,
    ...Config.EXECUTE_ONCE,
  });

  return useMemo(
    () => ({ editEM, data, isLoading, isError, error }),
    [editEM, data, isLoading, isError, error],
  );
};

export const useAllEventCode = (eventCode) => {
  let { data, error, isLoading, isFetching } = useQuery(
    [QueryKey.EVENT_CODES_LIST, eventCode],
    () => getAllEventCodes(),
    {
      refetchOnMount: true,
      refetchOnReconnect: true,
      enabled: eventCode,
    },
  );
  const uniqueEventCodes = useMemo(() => {
    return getAllEventCode(data);
  }, [data]);

  return {
    eventCodeList: uniqueEventCodes ,
    error,
    isLoading,
    isFetching
  }
}

const getAllEventCode = (data) => {
  let uniqueEventCodes = new Set()
  if (!data || !Array.isArray(data)) {
    return []
  }

  for (const eventMap of data) {
    if (!eventMap || !eventMap.events || !Array.isArray(eventMap.events)) {
      continue;
    }
    for (const events of eventMap.events) {
      const sourceId = events.sourceEventId
      if (sourceId) {
        uniqueEventCodes.add(sourceId)
      }
    }
  }
  return Array.from(uniqueEventCodes).map(Number).sort((a,b) => a - b);
}
